import { HttpClient } from '@angular/common/http';
import { IDataset } from '@app/core/model/datasets/dataset';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { BytesPipe } from '@app/core/utilities/pipes/bytes.pipe';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';
import { FastqQcStatus } from '@app/core/model/datasets/fastq-qc-status';
import { StatusToDisplayName } from '@app/core/utilities/pipes/status-to-display-name.pipe';
import _ from 'lodash';

export enum DatasetSearchColumnFields {
    Name = 'Name',
    Analysis = 'Analysis',
    Project = 'Project',
    Created = 'Created',
    Application = 'Application',
    Owner = 'Owner',
    Size = 'Size',
    QcStatus = 'QcStatus'
}


export class DatasetSearchGridOptionsFormatter extends SearchGridOptionsFormatter<DatasetSearchColumnFields> {
    public projectFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
        public resourceDictionaryService: SearchResourceDictionaryService,
        public bsshDatePipe: BsshDatePipe,
        public bytesPipe: BytesPipe,
        public http: HttpClient,
        public statusToDisplayName: StatusToDisplayName
    ) {
        super(resourceDictionaryService, SearchResourceType.Dataset);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
        return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
            params: {
                limit: '50',
                scope: 'projects',
                sortBy: 'ModifiedOn',
                sortDir: 'desc',
                query: arg ? `Project.Name:"${arg}"` : ''
            },
            withCredentials: true
        });
    }

    updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
        return this.getProjectFilterOptions(arg).pipe(
          map((response) => {
            return this.toProjectFilterOptions(response);
          })
        );
    }

    bindFilterUpdateCallbacksToCurrentContext() {
        this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
    }

    toProjectFilterOptions(result): FilterOption[] {
        const projects: [] = result.Response.Items;
        const filteroptions: FilterOption[] = [];
        projects.forEach((item: any) => {
          const filterOption: FilterOption = {
            displayName: item.Project.Name,
            filterValue: item.Project.Id
          };
          filteroptions.push(filterOption);

        });
        return filteroptions;
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<DatasetSearchColumnFields> {
        const inclusiveColumns: DatasetSearchColumnFields[] = [
            DatasetSearchColumnFields.Name,
            DatasetSearchColumnFields.Analysis,
            DatasetSearchColumnFields.Application,
            DatasetSearchColumnFields.Owner,
            DatasetSearchColumnFields.Created
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<DatasetSearchColumnFields>[] = [
            {
              colId: DatasetSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions() {
        const defaultColumnDefinitions = [
            {
                headerName: 'Name',
                field: 'Dataset.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.Dataset && params.data.Dataset.AppSession && !params.data.Dataset.IsDeleted && !params.data.Dataset.IsArchived) {
                        const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IDataset;
                        const analysis = resource.AppSession;
                        return DataTableUtilities.getRadLinkParams(
                            resource.Name,
                            BsApiEndPoints.getAppResultRoute(analysis.Id, resource.Id)
                        );
                    } else if(params.data && params.data.Dataset) {
                        const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IDataset;
                        return DataTableUtilities.getRadLinkParams(resource.Name);
                    } else {
                        return {};
                    }
                },
                colId: DatasetSearchColumnFields.Name,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Analysis',
                field: 'Dataset.AppSession.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.Dataset && params.data.Dataset.AppSession) {
                        const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IDataset;
                        const analysis = resource.AppSession;
                        return DataTableUtilities.getRadLinkParams(
                            analysis.Name,
                            BsApiEndPoints.getAnalysesRoute(analysis.Id)
                        );
                    } else {
                        return {};
                    }
                },
                colId: DatasetSearchColumnFields.Analysis,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Project',
                field: 'Dataset.Project.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.Dataset && params.data.Dataset.Project) {
                        const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IDataset;
                        const project = resource.Project;
                        return DataTableUtilities.getRadLinkParams(
                            project.Name,
                            BsApiEndPoints.getProjectDetailsRoute(project.Id)
                        );
                    } else {
                        return {};
                    }
                },
                colId: DatasetSearchColumnFields.Project,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback,
            },
            {
                headerName: 'Created',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return this.bsshDatePipe.transform(params.data.Dataset.DateCreated);
                  },
                filterActive: false,
                sortable: false,
                colId: DatasetSearchColumnFields.Created,
                cellRenderer: 'span',
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE
              },
              {
                headerName: 'Application',
                colId: DatasetSearchColumnFields.Application,
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if (params.data && params.data.Dataset && params.data.Dataset.AppSession && params.data.Dataset.AppSession.Application) {
                        const application = params.data.Dataset.AppSession.Application;
                        return DataTableUtilities.getRadLinkParams(
                            application.Name, 
                            `/apps/${application.Id}`
                        );
                    } else {
                        return {};
                    }
                },
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Owner',
                field: 'Dataset.UserOwnedBy.Name',
                cellRenderer: 'span',
                colId: DatasetSearchColumnFields.Owner,
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Size',
                valueGetter: (params) => {
                    if (params.data && params.data.Dataset && params.data.Dataset.TotalSize) {
                        return this.bytesPipe.transform(params.data.Dataset.TotalSize, 2);
                    }
                    return this.bytesPipe.transform(0, 2);
                },
                cellRenderer: 'span',
                colId: DatasetSearchColumnFields.Size,
                sortable: false,
                flex: 1.5,
                minWidth: ColumnMinWidth.SIZE
            },
            {
                headerName: 'Status',
                valueGetter: params => {
                    if (!params || !params.data) {
                        return;
                    }

                    const value = _.get(params.data.Dataset, 'QcStatus', null) as string;

                    return !value || value.toLowerCase() === 'undefined' ? '--' : this.statusToDisplayName.transform(value);
                },
                cellStyle: (params) => {
                    if (!params || !params.data) {
                        return;
                    }

                    const value = _.get(params.data, 'Dataset.QcStatus', null) as string;

                    return value === FastqQcStatus.QC_FAILED ? { fontWeight: 'bold' } : null;
                },
                cellRenderer: 'span',
                flex: 1.5,
                minWidth: 160,
                maxWidth: 200,
                resizable: false,
                colId: DatasetSearchColumnFields.QcStatus
            },
        ];
        return defaultColumnDefinitions;
    }
}
